import { useTranslation } from 'react-i18next'

import type { ColorScheme } from '@hivewatch/dark'

import { FilterDrawer } from '@/components/filter-drawer'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { removeNullValues } from '@/redux/incidents/utils'
import {
  selectGuardToursFacilityIdsFilter,
  selectGuardToursGuardIdsFilter,
  selectGuardToursStatusFilter,
  selectGuardToursSubmittedAtRangeFilter,
  setGuardToursFacilityIdsFilter,
  setGuardToursGuardIdsFilter,
  setGuardToursStatusFilter,
  setGuardToursSubmittedAtRangeFilter,
} from '@/redux/ui/uiSlice'
import { BUTTON_PRESS, trackEvent } from '@/utils/analytics'

import type { GuardToursFilterFormInputsI } from '../types/form'
import { DarkGuardToursFilterForm } from './DarkGuardToursFilterForm'

const FILTER_FIELD_NAMES: Array<keyof GuardToursFilterFormInputsI> = [
  'facilityIds',
  'guardIds',
  'status',
  'submittedAt',
]

interface DarkGuardToursFilterDrawerIProps {
  isOpen: boolean
  onClose: () => void
  colorScheme: ColorScheme
}

export const DarkGuardToursFilterDrawer = ({
  isOpen,
  onClose,
  colorScheme,
}: DarkGuardToursFilterDrawerIProps) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const facilityIdsFilterValue = useAppSelector(
    selectGuardToursFacilityIdsFilter
  )
  const guardIdsFilterValue = useAppSelector(selectGuardToursGuardIdsFilter)
  const statusFilterValue = useAppSelector(selectGuardToursStatusFilter)
  const submittedAtFilterValue = useAppSelector(
    selectGuardToursSubmittedAtRangeFilter
  )

  const defaultValues: GuardToursFilterFormInputsI = {
    facilityIds: facilityIdsFilterValue ?? null,
    guardIds: guardIdsFilterValue ?? null,
    status: statusFilterValue ?? null,
    submittedAt: submittedAtFilterValue ?? null,
  }

  const onApply = (values: GuardToursFilterFormInputsI) => {
    // Multi filters persist as null (not []) when empty, matching the legacy
    // drawer and the count selector's `?.length` check.
    dispatch(
      setGuardToursFacilityIdsFilter(
        values.facilityIds?.length ? values.facilityIds : null
      )
    )
    dispatch(
      setGuardToursGuardIdsFilter(
        values.guardIds?.length ? values.guardIds : null
      )
    )
    dispatch(
      setGuardToursStatusFilter(values.status?.length ? values.status : null)
    )
    dispatch(setGuardToursSubmittedAtRangeFilter(values.submittedAt ?? null))

    trackEvent('Guard Tours View: Filters Applied', {
      filters: removeNullValues({
        facilityIds: values.facilityIds,
        guardIds: values.guardIds,
        status: values.status,
        submittedAt: values.submittedAt,
      }),
    })
  }

  return (
    <FilterDrawer<GuardToursFilterFormInputsI>
      colorScheme={colorScheme}
      defaultValues={defaultValues}
      fieldNames={FILTER_FIELD_NAMES}
      isOpen={isOpen}
      onApply={onApply}
      onClear={() =>
        trackEvent(`${BUTTON_PRESS} Guard Tours Filter Drawer - Clear Filters`)
      }
      onClose={onClose}
      title={t('guard_tours:filter_drawer.title')}
    >
      {(methods) => (
        <DarkGuardToursFilterForm
          colorScheme={colorScheme}
          control={methods.control}
        />
      )}
    </FilterDrawer>
  )
}
